import MenuBook from "@mui/icons-material/MenuBook";
import {
  Autocomplete,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  type FormControlProps,
  TextField,
  Typography,
} from "@mui/material";
import { type FC, useState } from "react";
import { toAnalyticsIdSegment } from "../../../utils/analytics-id";
import { capitalizeEachWord } from "../../../utils/capitalize";
import { formatDate, fromISO8601_DATE } from "../../../utils/iso8601";
import { useToTarget } from "../../to/use-to-target";
import type { EventKey } from "../server/supabase-event-service";
import { EventForm } from "./event-form";

const AddButton: FC<{ onClick: () => void; buttonAction: string }> = ({
  onClick,
  buttonAction,
}) => {
  return (
    <>
      <ButtonBase
        data-analytics-id={`add-button-${toAnalyticsIdSegment(buttonAction)}`}
        onClick={onClick}
        sx={{
          width: "100%",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          py: 1,
          px: 3,
          justifyContent: "flex-start",
        }}
      >
        <Typography sx={{ color: "primary.main", fontWeight: "bold" }}>{buttonAction}</Typography>
      </ButtonBase>
    </>
  );
};

/** Select from events */
export const EventSelector: FC<
  FormControlProps & {
    value: EventKey | null;
    events: EventKey[];
    onChangeEvent: (event: EventKey | null, events: EventKey[]) => void;
  }
> = ({ value, onChangeEvent, events, ...props }) => {
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const { isManagerConnection, toTarget } = useToTarget();

  return (
    <>
      {events.length > 0 ? (
        <FormControl {...props}>
          <Autocomplete
            id="event-select"
            sx={{ minWidth: 390 }}
            disabled={props.disabled}
            value={events.find((e) => e.key === value?.key) ?? null}
            options={[
              ...events.filter((e) => !e.calendar),
              ...(isManagerConnection
                ? []
                : [
                    ...events.filter((e) => e.calendar && (e.has_schedule_event || e.library)),
                    ...events.filter(
                      (e) => e.calendar && !e.has_schedule_event && value?.key === e.key,
                    ),
                    {
                      key: "create-new-event",
                      title: "",
                      calendar: true,
                      date: "",
                      has_schedule_event: true,
                    } as unknown as EventKey,
                  ]),
            ]}
            renderInput={(params) => (
              <TextField
                // This is backwards compatible with the target_manager flag
                label={`${toTarget === "connection" ? "Subjects’ " : ""}Event`}
                {...params}
              />
            )}
            getOptionLabel={(option) => displayText(option)}
            groupBy={(option) => (option.calendar ? "Custom Events" : "Automated Events")}
            onChange={(event, v) => onChangeEvent(v ?? null, events)}
            renderOption={(renderProps, option) =>
              option.key === "create-new-event" ? (
                <AddButton
                  key="create-new-event-button"
                  buttonAction="Create new event"
                  onClick={() => setCreateEventOpen(true)}
                />
              ) : (
                <Box component="li" {...renderProps} key={option.key}>
                  {displayText(option)}
                  {option.key.endsWith("_library") && (
                    <MenuBook sx={{ paddingLeft: 1 }} color="primary" />
                  )}
                </Box>
              )
            }
          />
        </FormControl>
      ) : null}
      <Dialog open={createEventOpen} onClose={() => setCreateEventOpen(false)} maxWidth="xl">
        <DialogTitle>Create new event</DialogTitle>
        <DialogContent>
          <EventForm
            onCancel={() => setCreateEventOpen(false)}
            onChange={(event) => {
              const newEvent = {
                calendar: true,
                key: event.key,
                title: event.title ?? "",
                date: event.date,
                library: false,
                repeat: event.repeat === "never" ? null : event.repeat,
                number_of_repeats: event.repeat === "never" ? 0 : 100,
                allow_initial: true,
                allow_repeats: event.repeat !== "never",
                has_schedule_event: true,
              };
              const newEvents = [...events, newEvent];
              setCreateEventOpen(false);
              onChangeEvent(newEvent, newEvents);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const displayText = (event: EventKey | undefined): string => {
  if (!event) return "";
  if (!event.calendar) return capitalizeEachWord(event.title);

  const date = event.date === undefined ? "" : formatDate(fromISO8601_DATE(event.date));

  return event.has_schedule_event ? `${capitalizeEachWord(event.title)} (${date})` : date;
};
